/**
 * The XOP price book, which is also the product catalogue — because since
 * 2026-09-13 they are the same list.
 *
 * Two levels: a product (USN, RFCS, UGN…) and the features inside it, each
 * carrying its own part number, category, unit and PRICE. Before this there
 * were three levels and two lists — a feature had versions under it, and its
 * price lived on a separate price-list row that somebody had to keep in step
 * by hand. Quoting meant choosing which of the two lists to pick from.
 *
 * This file is the data orchestrator: the products list query, the dialog
 * state, the create/edit/delete mutations, and the layout. The rows live in
 * `products-panel/ProductRow` + `FeatureRow`; the product dialog is the shared
 * `EntityFormDialog` shell and the feature dialog is `FeatureDialog` (it needs
 * selects and a mode-dependent price field, which that shell does not do).
 */
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

import { useCan } from "../../../../auth/permissions.js";
import { ConfirmDialog } from "../../../../components/ui/confirm-dialog.js";
import { PageLoader } from "@/components/ui/page-loader";
import { ApiError } from "../../../../lib/api.js";
import {
  type FeatureWrite,
  type Product,
  type ProductFeature,
  createFeature,
  createProduct,
  deleteFeature,
  deleteProduct,
  listProducts,
  updateFeature,
  updateProduct,
} from "./products-api.js";
import { EntityFormDialog } from "./products-panel/EntityFormDialog.js";
import { FeatureDialog } from "./products-panel/FeatureDialog.js";
import { ProductRow } from "./products-panel/ProductRow.js";

type NameCode = { name: string; code: string };

export function ProductsPanel(): JSX.Element {
  const canEdit = useCan("admin:users");
  const qc = useQueryClient();
  const productsKey = ["admin", "products"] as const;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: productsKey,
    queryFn: listProducts,
  });

  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Product dialog
  const [prodOpen, setProdOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [prodForm, setProdForm] = useState<NameCode>({ name: "", code: "" });
  // Feature dialog — one form for the feature AND its price.
  const [featOpen, setFeatOpen] = useState(false);
  const [featProductId, setFeatProductId] = useState<number | null>(null);
  const [featProductName, setFeatProductName] = useState("");
  const [editingFeature, setEditingFeature] = useState<ProductFeature | null>(null);

  const [formError, setFormError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<
    | { kind: "product"; id: number; label: string }
    | { kind: "feature"; id: number; label: string }
    | null
  >(null);

  const products = data ?? [];
  // Features live in the per-product detail query (["admin","product",id]),
  // and invalidating only the products LIST left those stale until a full page
  // reload (the "appears only after refresh" bug). Invalidate both families
  // (prefix match covers every id) so create/edit/delete reflects immediately.
  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ["admin", "products"] });
    void qc.invalidateQueries({ queryKey: ["admin", "product"] });
    // The quote/invoice picker and anything else reading the price book off
    // the projection endpoint must not keep showing yesterday's price.
    void qc.invalidateQueries({ queryKey: ["price-list"] });
  };

  function toErr(e: unknown, fallback: string): string {
    return e instanceof ApiError && e.message ? e.message : fallback;
  }

  const saveProduct = useMutation({
    mutationFn: async () => {
      const body = { name: prodForm.name, ...(prodForm.code ? { code: prodForm.code } : {}) };
      if (editingProduct) await updateProduct(editingProduct.id, body);
      else await createProduct(body);
    },
    onSuccess: () => {
      setProdOpen(false);
      setEditingProduct(null);
      setProdForm({ name: "", code: "" });
      setFormError(null);
      invalidateAll();
    },
    onError: (e) => setFormError(toErr(e, "Save failed")),
  });

  const saveFeature = useMutation({
    mutationFn: async (body: FeatureWrite) => {
      if (editingFeature) await updateFeature(editingFeature.id, body);
      else if (featProductId != null) await createFeature(featProductId, body);
    },
    onSuccess: () => {
      setFeatOpen(false);
      setEditingFeature(null);
      setFeatProductId(null);
      setFormError(null);
      invalidateAll();
    },
    onError: (e) => setFormError(toErr(e, "Save failed")),
  });

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      if (pendingDelete.kind === "product") await deleteProduct(pendingDelete.id);
      else await deleteFeature(pendingDelete.id);
    } catch {
      throw new Error("delete-failed");
    }
    invalidateAll();
  }

  function toggle(id: number): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Card>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          borderBottom: 1,
          borderColor: "divider",
          px: 2.5,
          py: 2,
        }}
      >
        {/* Heading + button, nothing else. A three-line explanation of what a
            product is used to sit under the heading and squeezed "New product"
            into a two-line pill at the edge (removed 2026-09-13). The rows
            below explain the model better than prose beside them could. */}
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}>
          Products and prices
        </Typography>
        {canEdit && (
          <Button
            variant="contained"
            size="small"
            aria-label="New product"
            startIcon={<Plus size={16} />}
            onClick={() => {
              setEditingProduct(null);
              setProdForm({ name: "", code: "" });
              setFormError(null);
              setProdOpen(true);
            }}
          >
            New product
          </Button>
        )}
      </Box>
      <CardContent sx={{ px: 2.5, py: 2 }}>
        {isLoading ? (
          <PageLoader />
        ) : isError ? (
          <Box sx={{ borderRadius: 1, border: 1, borderColor: "error.main", p: 2, fontSize: 14 }}>
            <Typography sx={{ mb: 1, color: "error.main" }}>
              Could not load products
              {error instanceof ApiError && error.message ? `: ${error.message}` : "."}
            </Typography>
            <Button variant="contained" size="small" onClick={() => void refetch()}>Retry</Button>
          </Box>
        ) : products.length === 0 ? (
          <Typography sx={{ py: 3, textAlign: "center", fontSize: 14, color: "text.disabled" }}>
            No products yet.{canEdit ? " Use “New product” to add one." : ""}
          </Typography>
        ) : (
          <Box sx={{ display: "grid", gap: 1 }}>
            {products.map((p) => (
              <ProductRow
                key={p.id}
                product={p}
                expanded={expanded.has(p.id)}
                canEdit={canEdit}
                onToggle={() => toggle(p.id)}
                onEdit={() => {
                  setEditingProduct(p);
                  setProdForm({ name: p.name, code: p.code ?? "" });
                  setFormError(null);
                  setProdOpen(true);
                }}
                onDelete={() =>
                  setPendingDelete({ kind: "product", id: p.id, label: p.name })
                }
                onAddFeature={() => {
                  setEditingFeature(null);
                  setFeatProductId(p.id);
                  setFeatProductName(p.name);
                  setFormError(null);
                  setFeatOpen(true);
                }}
                onEditFeature={(f) => {
                  setEditingFeature(f);
                  setFeatProductId(p.id);
                  setFeatProductName(p.name);
                  setFormError(null);
                  setFeatOpen(true);
                }}
                onDeleteFeature={(f) =>
                  setPendingDelete({ kind: "feature", id: f.id, label: f.name })
                }
              />
            ))}
          </Box>
        )}
      </CardContent>

      <EntityFormDialog
        open={prodOpen}
        onClose={() => setProdOpen(false)}
        title={editingProduct ? `Edit ${editingProduct.name}` : "New product"}
        description="A product (e.g. “USN”) groups the features customers buy."
        formId="product-form"
        fields={[
          { id: "prod-name", label: "Name *", value: prodForm.name, onChange: (v) => setProdForm({ ...prodForm, name: v }), required: true },
          { id: "prod-code", label: "Code (optional, 4 digits)", value: prodForm.code, onChange: (v) => setProdForm({ ...prodForm, code: v }), placeholder: "e.g. 1001" },
        ]}
        onSubmit={() => saveProduct.mutate()}
        isPending={saveProduct.isPending}
        submitLabel={editingProduct ? "Save" : "Create"}
        error={formError}
      />

      <FeatureDialog
        open={featOpen}
        feature={editingFeature}
        productName={featProductName}
        error={formError}
        saving={saveFeature.isPending}
        onClose={() => setFeatOpen(false)}
        onSave={(body) => saveFeature.mutate(body)}
      />

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title={`Delete ${pendingDelete?.kind ?? ""}?`}
        description={
          <>
            <strong>{pendingDelete?.label ?? ""}</strong>
            {pendingDelete?.kind === "product"
              ? " and all its features, with their prices,"
              : " and its price"}{" "}
            will be deleted permanently. This cannot be undone.
          </>
        }
        confirmLabel={`Delete ${pendingDelete?.kind ?? ""}`}
        onConfirm={confirmDelete}
      />
    </Card>
  );
}
